/* eslint-disable */
function PrivacyPolicy() {
  return (
    <div className="prose prose-gray max-w-none">
      <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Privacy Policy</h1>
      <p className="text-sm text-gray-700 mb-8">Effective date: April 2, 2026 · Last updated: April 2, 2026</p>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">1. Who We Are</h2>
        <p className="text-gray-600 leading-relaxed">
          Affiliar Ltd. (&quot;Affiliar&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;) operates the Affiliar affiliate management platform, accessible
          at affiliar.io. Affiliar Ltd. is the data controller responsible for the personal data you provide when
          using our Service.
        </p>
        <p className="text-gray-600 leading-relaxed mt-3">
          For any privacy-related enquiries, please contact our Data Protection Officer at{' '}
          <a href="mailto:dpo@affiliar.io" className="text-blue-600 underline">dpo@affiliar.io</a>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">2. Data We Collect</h2>
        <p className="text-gray-600 leading-relaxed">We collect the following categories of personal data:</p>

        <h3 className="text-base font-bold text-gray-700 mt-4 mb-2">2.1 Account &amp; Identity Data</h3>
        <ul className="list-disc list-inside space-y-1 text-gray-600">
          <li>Full name, email address, and phone number</li>
          <li>Company name, VAT/tax registration number, and business address</li>
          <li>Payment details (bank account or e-wallet information for commission payouts)</li>
          <li>Identity verification documents where required by law</li>
        </ul>

        <h3 className="text-base font-bold text-gray-700 mt-4 mb-2">2.2 Usage &amp; Technical Data</h3>
        <ul className="list-disc list-inside space-y-1 text-gray-600">
          <li>IP address, browser type, operating system, and device identifiers</li>
          <li>Pages visited, time on page, click paths, and session duration</li>
          <li>API call logs and feature usage patterns within the platform</li>
        </ul>

        <h3 className="text-base font-bold text-gray-700 mt-4 mb-2">2.3 Communication Data</h3>
        <ul className="list-disc list-inside space-y-1 text-gray-600">
          <li>Emails, support tickets, and chat messages exchanged with Affiliar</li>
          <li>Survey responses and feedback you voluntarily provide</li>
        </ul>

        <h3 className="text-base font-bold text-gray-700 mt-4 mb-2">2.4 Aggregated Performance Data</h3>
        <ul className="list-disc list-inside space-y-1 text-gray-600">
          <li>Referred player counts, conversion rates, NGR, and commission calculations (anonymised at the player level)</li>
          <li>Campaign and traffic source statistics tied to your affiliate tracking links</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">3. How We Use Your Data</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-gray-600 border border-gray-200 rounded-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-700">Purpose</th>
                <th className="text-left p-3 font-semibold text-gray-700">Legal Basis</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="p-3">Providing and maintaining the Affiliar platform</td>
                <td className="p-3">Performance of a contract (Art. 6(1)(b) GDPR)</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="p-3">Processing commission payments</td>
                <td className="p-3">Performance of a contract (Art. 6(1)(b) GDPR)</td>
              </tr>
              <tr>
                <td className="p-3">Identity verification and fraud prevention</td>
                <td className="p-3">Legal obligation (Art. 6(1)(c) GDPR)</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="p-3">Compliance with AML/KYC regulations</td>
                <td className="p-3">Legal obligation (Art. 6(1)(c) GDPR)</td>
              </tr>
              <tr>
                <td className="p-3">Platform analytics and performance improvements</td>
                <td className="p-3">Legitimate interests (Art. 6(1)(f) GDPR)</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="p-3">Sending transactional and service communications</td>
                <td className="p-3">Performance of a contract (Art. 6(1)(b) GDPR)</td>
              </tr>
              <tr>
                <td className="p-3">Sending marketing communications</td>
                <td className="p-3">Consent (Art. 6(1)(a) GDPR)</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="p-3">Resolving disputes and enforcing Terms</td>
                <td className="p-3">Legitimate interests (Art. 6(1)(f) GDPR)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">4. Data Sharing and Recipients</h2>
        <p className="text-gray-600 leading-relaxed">
          We do not sell your personal data. We may share your data with:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-600 mt-3">
          <li><strong>Operators:</strong> Operator programs you join may receive limited profile data (e.g., affiliate name and payment details) necessary to administer your participation in their programs.</li>
          <li><strong>Payment processors:</strong> To execute commission payouts securely.</li>
          <li><strong>Cloud infrastructure providers:</strong> Hosting and data storage under appropriate data processing agreements.</li>
          <li><strong>Analytics and monitoring tools:</strong> Used to improve platform stability and performance; data is processed under strict contractual protections.</li>
          <li><strong>Regulatory and law enforcement authorities:</strong> Where required by law, court order, or regulatory obligation.</li>
        </ul>
        <p className="text-gray-600 leading-relaxed mt-3">
          All third parties are required to handle your data in accordance with applicable law and our data processing
          agreements.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">5. International Data Transfers</h2>
        <p className="text-gray-600 leading-relaxed">
          Affiliar is headquartered in Malta (EU). Where we transfer personal data outside the European Economic Area,
          we ensure appropriate safeguards are in place — such as Standard Contractual Clauses (SCCs) approved by the
          European Commission — to provide an equivalent level of protection.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">6. Data Retention</h2>
        <p className="text-gray-600 leading-relaxed">
          We retain personal data for as long as your account is active and for a period thereafter as required to
          comply with legal obligations, resolve disputes, and enforce agreements. Typical retention periods:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-600 mt-3">
          <li>Account and identity data: 5 years after account closure (AML/KYC obligations)</li>
          <li>Financial transaction records: 7 years (tax and regulatory requirements)</li>
          <li>Platform usage logs: 12 months</li>
          <li>Marketing consent records: Until consent is withdrawn + 3 years</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">7. Your Rights</h2>
        <p className="text-gray-600 leading-relaxed">
          Under the GDPR and applicable data protection law, you have the right to:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-600 mt-3">
          <li><strong>Access</strong> — obtain a copy of the personal data we hold about you.</li>
          <li><strong>Rectification</strong> — request correction of inaccurate or incomplete data.</li>
          <li><strong>Erasure</strong> — request deletion of your data where it is no longer necessary, subject to legal retention obligations.</li>
          <li><strong>Restriction</strong> — request that we restrict processing in certain circumstances.</li>
          <li><strong>Data portability</strong> — receive your data in a structured, machine-readable format.</li>
          <li><strong>Object</strong> — object to processing based on legitimate interests or for direct marketing.</li>
          <li><strong>Withdraw consent</strong> — where processing is based on consent, withdraw it at any time without affecting prior processing.</li>
        </ul>
        <p className="text-gray-600 leading-relaxed mt-3">
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:dpo@affiliar.io" className="text-blue-600 underline">dpo@affiliar.io</a>. We will respond
          within 30 days. You also have the right to lodge a complaint with your supervisory authority.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">8. Security</h2>
        <p className="text-gray-600 leading-relaxed">
          We implement appropriate technical and organisational measures to protect your data against unauthorised
          access, loss, or disclosure. These include encryption in transit and at rest, access controls, and regular
          security assessments. However, no internet-based transmission is completely secure, and we cannot guarantee
          absolute security.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">9. Cookies</h2>
        <p className="text-gray-600 leading-relaxed">
          We use cookies and similar technologies on our platform. For full details, please read our{' '}
          <a href="/cookie-policy" className="text-blue-600 underline">Cookie Policy</a>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">10. Changes to This Policy</h2>
        <p className="text-gray-600 leading-relaxed">
          We may update this Privacy Policy from time to time. Material changes will be communicated via email or
          in-platform notification. The &quot;Last updated&quot; date at the top of this page reflects the most recent revision.
          We encourage you to review this Policy periodically.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">11. Contact</h2>
        <p className="text-gray-600 leading-relaxed">
          For data protection enquiries, please contact our Data Protection Officer:
        </p>
        <address className="not-italic text-gray-600 mt-2 space-y-1">
          <p className="font-medium">Affiliar Ltd. — Data Protection Officer</p>
          <p>Email: <a href="mailto:dpo@affiliar.io" className="text-blue-600 underline">dpo@affiliar.io</a></p>
        </address>
      </section>
    </div>
  );
}

export default PrivacyPolicy;
